import { useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useUploadAvatar, useDeleteAvatar } from '../lib/hooks';
import { avatarApi } from '../lib/api';
import { toast } from 'sonner';
import { User, Bell, Shield, Camera, Trash2, Loader2, Upload, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../lib/translations';

const SettingsPage = () => {
  const { user, updateUser, refreshUser } = useAuth();
  const { lang } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [profile, setProfile] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
  });
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    weeklyDigest: true,
  });
  
  // Avatar state
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  
  const uploadAvatar = useUploadAvatar();
  const deleteAvatar = useDeleteAvatar();
  
  const avatarUrl = user?.avatar_url 
    ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}?t=${Date.now()}`
    : null;

  const handleResetAll = () => {
    setShowResetConfirm(true);
    setResetConfirmText('');
  };

  const confirmResetAll = async () => {
    if (resetConfirmText !== 'SUPPRIMER TOUT') return;
    setResetting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('hav_token');
      await fetch(`${API_URL}/api/admin/reset-all`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setShowResetConfirm(false);
      toast.success('Toutes les données ont été supprimées avec succès.');
    } catch (err) {
      toast.error('Erreur lors de la suppression.');
    } finally {
      setResetting(false);
      setResetConfirmText('');
    }
  };

  const handleChangePassword = async () => {
    if (passwords.next !== passwords.confirm) return;
    setChangingPassword(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('hav_token');
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: passwords.current, new_password: passwords.next })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success('Mot de passe modifié avec succès');
      setShowPasswordForm(false);
      setPasswords({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast.error(err.message || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUser({
        first_name: profile.first_name.trim() || undefined,
        last_name: profile.last_name.trim() || undefined,
        phone: profile.phone.trim() || undefined,
      });
      toast.success(lang === 'de' ? 'Profil aktualisiert' : 'Profil mis à jour avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === 'de' ? 'Fehler beim Speichern' : 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };
  
  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(lang === 'de' ? 'Dateityp nicht zulässig (JPG, PNG, WebP)' : 'Type de fichier non autorisé (JPG, PNG, WebP)');
      return;
    }
    
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(lang === 'de' ? 'Datei zu groß (max. 2MB)' : 'Fichier trop volumineux (max 2MB)');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
      setSelectedFile(file);
    };
    reader.readAsDataURL(file);
  }, [lang]);
  
  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFileSelect(file);
    e.target.value = '';
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleFileSelect(file);
  };
  
  const handleUploadAvatar = async () => {
    if (!selectedFile) return;
    
    try {
      await uploadAvatar.mutateAsync(selectedFile);
      toast.success(lang === 'de' ? 'Profilbild aktualisiert' : 'Photo de profil mise à jour');
      setPreviewUrl(null);
      setSelectedFile(null);
      // Force refresh user data
      if (refreshUser) refreshUser();
      window.location.reload(); // Simple refresh to update all components
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === 'de' ? 'Fehler beim Hochladen' : 'Erreur lors de l\'upload'));
    }
  };
  
  const handleDeleteAvatar = async () => {
    try {
      await deleteAvatar.mutateAsync();
      toast.success(lang === 'de' ? 'Profilbild gelöscht' : 'Photo de profil supprimée');
      if (refreshUser) refreshUser();
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || (lang === 'de' ? 'Fehler beim Löschen' : 'Erreur lors de la suppression'));
    }
  };
  
  const cancelPreview = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
  };
  
  const getRoleLabel = (role) => {
    const roles = lang === 'de' ? {
      admin: 'Administrator',
      teacher: 'Lehrkraft',
      student: 'Studierende/r',
      partner: 'Partner',
      resp_peda: 'Pädagogischer Leiter',
      resp_admin: 'Verwaltungsleiter',
    } : {
      admin: 'Super Administrateur',
      teacher: 'Enseignant',
      student: 'Étudiant',
      partner: 'Partenaire',
      resp_peda: 'Resp. Pédagogique',
      resp_admin: 'Resp. Administratif',
    };
    return roles[role] || role;
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">{lang === 'de' ? 'Einstellungen' : 'Paramètres'}</h1>
        <p className="text-muted-foreground mt-1">{lang === 'de' ? 'Verwalten Sie Ihr Konto und Ihre Einstellungen' : 'Gérez votre compte et vos préférences'}</p>
      </div>

      {/* Avatar Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Camera className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">{lang === 'de' ? 'Profilbild' : 'Avatar'}</CardTitle>
              <CardDescription>{lang === 'de' ? 'Personalisieren Sie Ihr Profilbild' : 'Personnalisez votre avatar'}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {/* Current/Preview Avatar */}
            <div className="relative">
              <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
                <AvatarImage src={previewUrl || avatarUrl} />
                <AvatarFallback className="bg-primary text-white text-2xl font-bold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              {(avatarUrl || previewUrl) && !selectedFile && (
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full"
                  onClick={handleDeleteAvatar}
                  disabled={deleteAvatar.isPending}
                  data-testid="delete-avatar-btn"
                >
                  {deleteAvatar.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              )}
            </div>
            
            {/* Upload Area */}
            <div className="flex-1 w-full">
              {selectedFile ? (
                <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={cancelPreview}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Annuler
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleUploadAvatar}
                      disabled={uploadAvatar.isPending}
                      className="bg-primary hover:bg-primary/90"
                      data-testid="confirm-avatar-btn"
                    >
                      {uploadAvatar.isPending ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-1" />
                      )}
                      Enregistrer
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                    isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="avatar-dropzone"
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleInputChange}
                    accept=".jpg,.jpeg,.png,.webp"
                    className="hidden"
                  />
                  <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm font-medium">
                    {lang === 'de' ? 'Bild hier ablegen oder klicken zum Auswählen' : 'Glissez une image ici ou cliquez pour parcourir'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {lang === 'de' ? 'JPG, PNG oder WebP • Max. 2MB' : 'JPG, PNG ou WebP • Max 2MB'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">{lang === 'de' ? 'Profil' : 'Profil'}</CardTitle>
              <CardDescription>{lang === 'de' ? 'Ihre persönlichen Informationen' : 'Vos informations personnelles'}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{lang === 'de' ? 'Vorname' : 'Prénom'}</Label>
              <Input
                value={profile.first_name}
                onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                placeholder={lang === 'de' ? 'Ihr Vorname' : 'Votre prénom'}
                data-testid="settings-firstname-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{lang === 'de' ? 'Nachname' : 'Nom'}</Label>
              <Input
                value={profile.last_name}
                onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                placeholder={lang === 'de' ? 'Ihr Nachname' : 'Votre nom'}
                data-testid="settings-lastname-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{lang === 'de' ? 'Telefon' : 'Téléphone'}</Label>
            <Input
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              placeholder="+33 6 00 00 00 00"
              data-testid="settings-phone-input"
            />
          </div>
          <div className="space-y-2">
            <Label>{lang === 'de' ? 'E-Mail' : 'Email'}</Label>
            <Input value={user?.email || ''} disabled className="bg-muted" />
            <p className="text-xs text-muted-foreground">{lang === 'de' ? 'E-Mail kann nicht geändert werden' : "L'email ne peut pas être modifié"}</p>
          </div>
          <div className="space-y-2">
            <Label>{lang === 'de' ? 'Rolle' : 'Rôle'}</Label>
            <Input value={getRoleLabel(user?.role)} disabled className="bg-muted" />
          </div>
        </CardContent>
      </Card>

      {/* Notifications Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Bell className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">{lang === 'de' ? 'Benachrichtigungen' : 'Notifications'}</CardTitle>
              <CardDescription>{lang === 'de' ? 'Verwalten Sie Ihre Benachrichtigungseinstellungen' : 'Gérez vos préférences de notification'}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{lang === 'de' ? 'E-Mail-Benachrichtigungen' : 'Notifications par email'}</p>
              <p className="text-sm text-muted-foreground">{lang === 'de' ? 'Benachrichtigungen per E-Mail erhalten' : 'Recevez des notifications par email'}</p>
            </div>
            <Switch 
              checked={settings.emailNotifications}
              onCheckedChange={(checked) => setSettings({...settings, emailNotifications: checked})}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{lang === 'de' ? 'SMS-Benachrichtigungen' : 'Notifications SMS'}</p>
              <p className="text-sm text-muted-foreground">{lang === 'de' ? 'Wichtige Warnungen per SMS erhalten' : 'Recevez des alertes importantes par SMS'}</p>
            </div>
            <Switch 
              checked={settings.smsNotifications}
              onCheckedChange={(checked) => setSettings({...settings, smsNotifications: checked})}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{lang === 'de' ? 'Wöchentliche Zusammenfassung' : 'Résumé hebdomadaire'}</p>
              <p className="text-sm text-muted-foreground">{lang === 'de' ? 'Erhalten Sie wöchentlich eine Zusammenfassung' : 'Recevez un résumé chaque semaine'}</p>
            </div>
            <Switch 
              checked={settings.weeklyDigest}
              onCheckedChange={(checked) => setSettings({...settings, weeklyDigest: checked})}
            />
          </div>
        </CardContent>
      </Card>

      {/* Security Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-accent" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">{lang === 'de' ? 'Sicherheit' : 'Sécurité'}</CardTitle>
              <CardDescription>{lang === 'de' ? 'Verwalten Sie die Sicherheit Ihres Kontos' : 'Gérez la sécurité de votre compte'}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!showPasswordForm ? (
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setShowPasswordForm(true)}
              data-testid="change-password-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              {lang === 'de' ? 'Passwort ändern' : 'Changer le mot de passe'}
            </Button>
          ) : (
            <div className="space-y-4 p-4 bg-muted/30 rounded-xl border border-muted">
              <div className="space-y-2">
                <Label>{lang === 'de' ? 'Aktuelles Passwort' : 'Mot de passe actuel'}</Label>
                <Input
                  type="password"
                  value={passwords.current}
                  onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                  placeholder="••••••••"
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{lang === 'de' ? 'Neues Passwort' : 'Nouveau mot de passe'}</Label>
                <Input
                  type="password"
                  value={passwords.next}
                  onChange={(e) => setPasswords({ ...passwords, next: e.target.value })}
                  placeholder="••••••••"
                  data-testid="new-password-input"
                />
                <p className="text-xs text-muted-foreground">Minimum 6 caractères</p>
              </div>
              <div className="space-y-2">
                <Label>{lang === 'de' ? 'Passwort bestätigen' : 'Confirmer le mot de passe'}</Label>
                <Input
                  type="password"
                  value={passwords.confirm}
                  onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                  placeholder="••••••••"
                  data-testid="confirm-password-input"
                />
                {passwords.next && passwords.confirm && passwords.next !== passwords.confirm && (
                  <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                )}
              </div>
              <div className="flex gap-3 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowPasswordForm(false); setPasswords({ current: '', next: '', confirm: '' }); }}
                >
                  Annuler
                </Button>
                <Button
                  size="sm"
                  className="bg-primary hover:bg-primary/90"
                  disabled={changingPassword || !passwords.current || passwords.next !== passwords.confirm || passwords.next.length < 6}
                  onClick={handleChangePassword}
                  data-testid="save-password-btn"
                >
                  {changingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {lang === 'de' ? 'Speichern' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {lang === 'de' ? 'Speichern...' : 'Enregistrement...'}
            </>
          ) : (
            lang === 'de' ? 'Speichern' : 'Enregistrer'
          )}
        </Button>
      </div>

      {/* Zone dangereuse — Admin seulement */}
      {user?.role === 'admin' && (
        <Card className="border-2 border-red-200 bg-red-50/30">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <CardTitle className="font-heading text-lg text-red-700">Zone dangereuse</CardTitle>
                <CardDescription className="text-red-500">Ces actions sont irréversibles</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-red-200">
              <div>
                <p className="font-semibold text-gray-900">Supprimer toutes les données</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Supprime tous les étudiants, cours, dossiers, paiements et messages.<br />
                  <span className="text-green-700 font-medium">Seul le compte ndedi.victoire@speak-move.com est préservé.</span>
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={handleResetAll}
                disabled={resetting}
                className="ml-4 shrink-0"
                data-testid="reset-all-btn"
              >
                {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Supprimer tout
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-center text-gray-900 mb-2">Confirmation requise</h3>
            <p className="text-gray-600 text-sm text-center mb-4">
              Cette action supprimera <strong>définitivement</strong> toutes les données de l'application
              (étudiants, cours, dossiers, paiements, messages...).<br /><br />
              Tapez <strong className="text-red-600">SUPPRIMER TOUT</strong> pour confirmer.
            </p>
            <Input
              value={resetConfirmText}
              onChange={(e) => setResetConfirmText(e.target.value)}
              placeholder="SUPPRIMER TOUT"
              className="mb-4 text-center font-mono border-red-300 focus:border-red-500"
              data-testid="reset-confirm-input"
            />
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => { setShowResetConfirm(false); setResetConfirmText(''); }}>
                Annuler
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                disabled={resetConfirmText !== 'SUPPRIMER TOUT' || resetting}
                onClick={confirmResetAll}
                data-testid="reset-confirm-btn"
              >
                {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirmer la suppression
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
